import { useEffect, useState } from "react";
import { ApiError } from "../../api/client";
import type { Authorization, CustomerDetail, CustomerRow, NewsletterAsk } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { Dialog } from "../../ui/Dialog";
import { formatPhone } from "../../lib/phone";
import { zoneLabel } from "../sale/customer";

interface Props {
  authorization: Authorization;
  customer: CustomerRow;
  onSaved: (customer: CustomerDetail) => void;
  onClose: () => void;
}

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const day = (iso: string) => {
  const date = new Date(iso);
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getFullYear())}`;
};

const sameAddress = (one: string, other: string) => one.trim().toLowerCase() === other.trim().toLowerCase();

/**
 * A customer, as in the prototype: the phone stays, the rest can change; the points at most a full
 * card; the internal note only for the staff. Below: orders, total in Tafo, last order, origin.
 *
 * The newsletter is the customer's own yes (Furkan, 30/10/2026: "b oké"): switched on here, the customer gets a
 * mail to confirm it ("Bevestigingsmail verstuurd") and shows "wacht op bevestiging" until he does. A yes holds for
 * the address it was given for: another address asks again.
 */
export function CustomerDialog({ authorization, customer, onSaved, onClose }: Props) {
  const { api, delivery, promotions, toast, report } = useTill();
  const text = t.customersAdmin.dialog;
  const goal = promotions?.points.goal ?? 11;
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [draft, setDraft] = useState({
    name: customer.name,
    email: customer.email,
    street: customer.street,
    number: customer.number,
    box: customer.box,
    zone: customer.zone,
    points: String(customer.points),
    // on while the customer has not said yes yet: saving again sends no new mail within a day of the last one
    newsletter: customer.newsletter || customer.newsletterPending,
    note: customer.note,
  });
  // the customer's yes, or the link he got, is for the address he has now: another one asks again
  const sameEmail = sameAddress(draft.email, customer.email);
  const waiting = draft.newsletter && sameEmail && customer.newsletterPending;
  const asks = draft.newsletter && !(sameEmail && (customer.newsletter || customer.newsletterPending));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (change: Partial<typeof draft>) => {
    setDraft((current) => ({ ...current, ...change }));
  };

  useEffect(() => {
    let current = true;
    api
      .request<{ customer: CustomerDetail }>("GET", `/device/customers/${customer.id}`, {
        staffToken: authorization.token,
      })
      .then((answer) => {
        if (current) setDetail(answer.customer);
      })
      .catch(report);
    return () => {
      current = false;
    };
  }, [api, authorization.token, customer.id, report]);

  const save = async () => {
    const email = draft.email.trim();
    if (email && !EMAIL.test(email)) {
      setError(text.badEmail);
      return;
    }
    setBusy(true);
    try {
      const answer = await api.request<{ customer: CustomerDetail; confirmation: NewsletterAsk | null }>(
        "PUT",
        `/device/customers/${customer.id}`,
        {
          staffToken: authorization.token,
          body: {
            name: draft.name.trim(),
            email,
            street: draft.street.trim(),
            number: draft.number.trim(),
            box: draft.box.trim(),
            zone: draft.zone.trim(),
            // prototype: 0 to a full card
            points: Math.max(0, Math.min(goal, Number.parseInt(draft.points, 10) || 0)),
            newsletter: draft.newsletter,
            note: draft.note.trim(),
          },
        },
      );
      if (answer.confirmation === "sent") toast(text.confirmationSent);
      else if (answer.confirmation === "too_soon") toast(text.confirmationTooSoon, "bad");
      else if (answer.confirmation === "too_many") toast(text.confirmationTooMany, "bad");
      else toast(text.saved);
      onSaved(answer.customer);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  };

  const input = (key: "name" | "email" | "street" | "number" | "box", label: string, mode?: "email") => (
    <label>
      {label}
      <input
        value={draft[key]}
        inputMode={mode}
        maxLength={key === "number" || key === "box" ? 20 : 200}
        onChange={(event) => {
          set({ [key]: event.target.value });
        }}
      />
    </label>
  );
  const zones = (delivery?.zones ?? []).map((zone) => zoneLabel(zone, delivery?.zones ?? []));

  return (
    <Dialog
      title={customer.name || text.customer}
      size="wide"
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {text.cancel}
          </button>
          <button type="button" className="btn primary" disabled={busy} onClick={() => void save()}>
            {text.save}
          </button>
        </>
      }
    >
      <div className="sfrm">
        <label>
          {text.phone}
          <input value={formatPhone(customer.phone)} disabled />
        </label>
        {input("name", text.name)}
        {input("email", text.email, "email")}
        {input("street", text.street)}
        {input("number", text.number)}
        {input("box", text.box)}
        <label>
          {text.zone}
          <select
            value={draft.zone}
            onChange={(event) => {
              set({ zone: event.target.value });
            }}
          >
            {zones.map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
            {!zones.includes(draft.zone) && <option value={draft.zone}>{draft.zone}</option>}
          </select>
        </label>
        <label>
          {text.points}
          <input
            type="number"
            min={0}
            max={goal}
            value={draft.points}
            onChange={(event) => {
              set({ points: event.target.value });
            }}
          />
        </label>
        <label className="inl">
          <input
            type="checkbox"
            checked={draft.newsletter}
            onChange={(event) => {
              set({ newsletter: event.target.checked });
            }}
          />{" "}
          {text.newsletter}
          {waiting && <span className="pill">{text.newsletterPending}</span>}
          {asks && <span className="note">{text.newsletterAsk}</span>}
        </label>
      </div>
      <label className="cnote">
        <span>
          {text.note} <span className="note">{text.noteHint}</span>
        </span>
        <textarea
          rows={2}
          value={draft.note}
          maxLength={500}
          onChange={(event) => {
            set({ note: event.target.value });
          }}
        />
      </label>
      <div className="kvs cstats">
        <div>
          <span>{text.orders}</span>
          <b>{detail?.orders ?? customer.orders}</b>
        </div>
        {detail && detail.totalCents > 0 && (
          <div>
            <span>{text.total}</span>
            <b>{formatEuro(detail.totalCents)}</b>
          </div>
        )}
        {detail?.lastOrder && (
          <div>
            <span>{text.last}</span>
            <b>
              {day(detail.lastOrder.at)} · {detail.lastOrder.text}
            </b>
          </div>
        )}
      </div>
      <p className="pinmsg left" role="alert">
        {error}
      </p>
    </Dialog>
  );
}
